import { createClient } from '@supabase/supabase-js';
import type { LockFunc } from '@supabase/auth-js';
import type { Database } from './database.types';

const url = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.trim() ?? '';
const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim() ?? '';

/** False when env is missing — avoid calling the API until .env is set. */
export const isSupabaseConfigured = Boolean(url && anonKey);

/**
 * Serialize all auth storage/refresh work in this tab without `navigator.locks`.
 * Plain `await fn()` is not enough: overlapping getSession/refresh calls must queue
 * or GoTrueClient's internal `lockAcquired` / pending queue can misbehave.
 */
let authLockTail = Promise.resolve();
const inProcessAuthLock: LockFunc = async (_name, _acquireTimeout, fn) => {
  const run = authLockTail.then(() => fn());
  authLockTail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
};

/**
 * Valid Supabase client. If env vars are missing, uses a non-networking-safe placeholder
 * so the app bundle still loads; all calls should check `isSupabaseConfigured` first where needed.
 */
export const supabase = createClient<Database>(
  url || 'https://invalid-placeholder.supabase.co',
  anonKey || 'invalid-placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      lock: inProcessAuthLock,
    },
  },
);

/**
 * `@supabase/supabase-js` does not forward `lockAcquireTimeout` into the Auth client
 * constructor (only a subset of auth fields is passed). Set on the instance so long
 * waits don't fight the default 5s navigator-lock recovery path in older paths.
 */
{
  const auth = supabase.auth as unknown as { lockAcquireTimeout?: number };
  if (typeof auth.lockAcquireTimeout === 'number') {
    auth.lockAcquireTimeout = 60_000;
  }
}
